/**
 * Catalog tree derivation (PURE). Builds a Product → Brand → Model tree from the existing
 * catalog data used by the Mockups funnel and the Templates surfaces:
 * `client.listTemplates()` + `client.listPhoneModels()`. Placement is TEMPLATE-DRIVEN — a
 * product/brand/model appears only when a template exists for it (this is a template
 * selector, not a device inventory). `listPhoneModels()` resolves each template's
 * `phoneModelId` to a `{ brand, model, slug }` label; when that fails we fall back to the
 * template's `display.model` ("<brand> <model>"), and finally to an "Unassigned" bucket so a
 * template is never silently dropped.
 *
 * Kept framework-free so the tree's rules are unit-tested without a DOM. Reuses
 * `productTypesOf` from ../funnel.js for the humanized product label.
 */
import type { PhoneModelOption, ProductTemplate } from "@genz/platform";
import { productTypesOf } from "../funnel.js";
import { catalogSlug } from "../../catalog/names.js";

export interface ModelNode {
  /** Stable selection key: the phone model id, else `unassigned:<label>`. */
  key: string;
  label: string;
  brand: string;
  /** Humanized product-type label this model sits under. */
  product: string;
  slug: string;
  templates: ProductTemplate[];
  count: number;
}

export interface BrandNode {
  key: string;
  label: string;
  count: number;
  models: ModelNode[];
}

export interface ProductNode {
  /** The raw `display.category` key. */
  key: string;
  label: string;
  count: number;
  brands: BrandNode[];
}

const UNASSIGNED = "Unassigned";

/**
 * The PRODUCT key for a template with no product type (WS-B B-5 / F-18).
 *
 * A leading space cannot collide with a real `product_type`, which the cloud emits as a
 * slug. Before this, `if (!productKey) continue` dropped such a row from every node and every
 * count while the table — which renders the full set pre-selection — still listed it, so the
 * tree and the table disagreed about how many templates exist. The module docstring had
 * promised this bucket since it was written; it only ever existed for the MODEL.
 */
const UNASSIGNED_PRODUCT = " unassigned";

/** The trimmed phone-model id, or "" when absent. */
const modelIdOf = (t: ProductTemplate): string => t.phoneModelId?.trim() ?? "";

/** Resolve a template's brand/model/slug: phone-model catalog first, then display.model. */
function resolveModel(
  t: ProductTemplate,
  byId: Map<string, PhoneModelOption>,
): { brand: string; model: string; slug: string } {
  const pm = byId.get(modelIdOf(t));
  if (pm) return { brand: pm.brand, model: pm.model, slug: pm.slug };
  const label = (t.display?.model ?? "").trim();
  if (label) {
    const [brand, ...rest] = label.split(/\s+/);
    const model = rest.join(" ") || label;
    // `catalogSlug`, not a private rule: this value is what `BatchContents` hands
    // `selectionFromFunnel` as `labels.phoneSlug`, where it OVERRIDES the resolver's own and
    // becomes the SKU's first field. The Latin-only rule that used to live here reduced
    // "小米 14" to "14", so every Xiaomi model missing from the cache collided on one SKU and
    // one output path — the silent overwrite `catalogSlug` was made unicode-aware to end,
    // reached through this door instead (closer 3).
    return { brand, model, slug: catalogSlug(label) };
  }
  return { brand: UNASSIGNED, model: UNASSIGNED, slug: "" };
}

export function buildCatalogTree(
  templates: ProductTemplate[],
  phoneModels: PhoneModelOption[],
): ProductNode[] {
  const byId = new Map(phoneModels.map((p) => [p.id, p]));
  // Product label lookup (humanized) from the shared helper.
  const productLabel = new Map(productTypesOf(templates).map((o) => [o.key, o.label]));
  // The one bucket the existing `productLabel.get(key) ?? key` path needs to name it.
  productLabel.set(UNASSIGNED_PRODUCT, UNASSIGNED);

  // productKey → brandLabel → modelKey → ModelNode
  const products = new Map<string, Map<string, Map<string, ModelNode>>>();
  for (const t of templates) {
    // A template with no product type goes to the Unassigned bucket rather than being
    // dropped from every node and every count (F-18).
    const productKey = (t.display?.category ?? "").trim() || UNASSIGNED_PRODUCT;
    const product = productLabel.get(productKey) ?? productKey;
    const { brand, model, slug } = resolveModel(t, byId);
    const modelKey = modelIdOf(t) || `unassigned:${model}`;

    const brands = products.get(productKey) ?? new Map();
    products.set(productKey, brands);
    const models = brands.get(brand) ?? new Map<string, ModelNode>();
    brands.set(brand, models);
    const node =
      models.get(modelKey) ??
      { key: modelKey, label: model, brand, product, slug, templates: [], count: 0 };
    node.templates.push(t);
    node.count = node.templates.length;
    models.set(modelKey, node);
  }

  const byLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);
  /** Products sort by label, except the Unassigned bucket, which always sorts LAST. Keyed on
   *  the SENTINEL, not the label, so a real product called "Unassigned" is not demoted. */
  const byProduct = (a: ProductNode, b: ProductNode) => {
    const au = a.key === UNASSIGNED_PRODUCT ? 1 : 0;
    const bu = b.key === UNASSIGNED_PRODUCT ? 1 : 0;
    return au !== bu ? au - bu : byLabel(a, b);
  };
  return [...products.entries()]
    .map(([key, brands]) => {
      const brandNodes: BrandNode[] = [...brands.entries()]
        .map(([brandLabel, models]) => {
          const modelNodes = [...models.values()].sort(byLabel);
          return {
            key: brandLabel,
            label: brandLabel,
            models: modelNodes,
            count: modelNodes.reduce((n, m) => n + m.count, 0),
          };
        })
        .sort(byLabel);
      return {
        key,
        label: productLabel.get(key) ?? key,
        brands: brandNodes,
        count: brandNodes.reduce((n, b) => n + b.count, 0),
      };
    })
    .sort(byProduct);
}

/** Filter the tree to branches whose product/brand/model label contains `query`
 *  (case-insensitive). A blank query returns the input tree unchanged. */
export function filterCatalogTree(tree: ProductNode[], query: string): ProductNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return tree;
  const has = (s: string) => s.toLowerCase().includes(q);
  const out: ProductNode[] = [];
  for (const p of tree) {
    if (has(p.label)) { out.push(p); continue; }
    const brands: BrandNode[] = [];
    for (const b of p.brands) {
      if (has(b.label)) { brands.push(b); continue; }
      const models = b.models.filter((m) => has(m.label));
      // Partially-kept brand: recompute count from the surviving models.
      if (models.length) {
        brands.push({ ...b, models, count: models.reduce((n, m) => n + m.count, 0) });
      }
    }
    // Partially-kept product: recompute count from the surviving brands.
    if (brands.length) {
      out.push({ ...p, brands, count: brands.reduce((n, b) => n + b.count, 0) });
    }
  }
  return out;
}
